<template>
  <section v-if="submitPolicyInProcess">
    <h1>{{ submitPolicyInProcessHeader }}</h1>
    <p v-html="ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER"></p>
  </section>
  <PremiumImpactInformation v-else :showMidContent="premiumData.isPremiumImpacted">
    <!-- title slot: header -->
    <template #header>
      <h1>{{ $route.meta.title }}</h1>
    </template>
    <!-- info text below title slot: headerInfoText -->
    <template #headerInfoText>
      <!-- Page information - when there is premium impact -->
      <div v-if="premiumData.isPremiumImpacted" v-html="refData.premiumImpactInformationText.informationText" />
      <!-- Page information - when there is NO premium impact -->
      <div v-else v-html="refData.noPremiumImpactInformationText.informationText" />
    </template>
    <!-- Slot for previous/new premium info: (productPremiumContent) -->
    <template #productPremiumContent v-if="premiumData.isPremiumImpacted">
      <ProductSummaryCard>
        <template #left>
          <h3>{{ productName }}</h3>
          <div v-if="premiumData.oldPremiumAmount || premiumData.newPremiumAmount">
            <p v-if="premiumData.oldPremiumAmount && premiumData.oldFrequency">
              Previous premium:
              <strong>{{ formatToDisplayCurrencyPeriod(premiumData.oldPremiumAmount, premiumData.oldFrequency) }}</strong>
            </p>
            <p v-if="premiumData.newPremiumAmount && premiumData.newFrequency">
              New ongoing premium amount:
              <strong>{{ formatToDisplayCurrencyPeriod(premiumData.newPremiumAmount, premiumData.newFrequency) }}</strong>
            </p>
            <p id="calloutText" v-html="refData.premiumImpactCalloutText.informationText" />
          </div>

          <!-- Alert when previous premium cannot be displayed -->
          <Alert :message="refData.premiumImpactNoPreviousPremiumText.informationText" v-if="premiumData.oldPremiumAmount === null || premiumData.oldFrequency === null" />
          <div>
            <p v-html="refData.premiumImpactPremiumInformationText.informationText" />
          </div>
        </template>
        <template #right>
          <h4>Your new ongoing premium</h4>
          <p class="text-green bold no-wrap" v-if="premiumData.newPremiumAmount && premiumData.newFrequency">
            <span class="font-large">${{ premiumData.newPremiumAmount }}</span>
            / {{ premiumData.newFrequency }}
          </p>
        </template>
      </ProductSummaryCard>
    </template>
    <!-- Payment timeline slot: premiumAdjustmentTimeline -->
    <template #premiumAdjustmentTimeline v-if="premiumData.isPremiumImpacted">
      <ApiLoading
        class="api-loading"
        v-if="apiStatus.premiumAdjustmentTimeline.loading || apiStatus.premiumAdjustmentTimeline.fail"
        :apiError="apiStatus.premiumAdjustmentTimeline.fail"
      />
      <PremiumAdjustmentTimeline
        v-if="!apiStatus.premiumAdjustmentTimeline.loading && !apiStatus.premiumAdjustmentTimeline.fail"
        :refData="refDataInExpectedFormat"
        :premiumTimelineData="premiumTimelineData"
        @handlePayNowBtnClick="handlePayNowBtnClick"
      />
    </template>
    <!-- Related Articles -->
    <template v-if="areThereArticlesToShow" #relatedArticles>
      <h2 class="hidden">Related pages</h2>
      <QuickLinks :linksData="relatedArticles" />
    </template>
  </PremiumImpactInformation>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { QuickLinks, PremiumAdjustmentTimeline, ApiLoading, PremiumImpactInformation, Alert, ProductSummaryCard } from '@/components';
import { formatToDisplayCurrencyPeriod } from '@/utils';
import PremiumImpactServices from '@/services/PremiumImpact';
import { MY_COVER_MAKE_A_PAYMENT } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { ErrorStrings } from '@general/js/constants';
import { generateSubmitPolicyInProcessHeader } from '@/utils/DisplayUtils';
import PaymentDetailsMixin from './PaymentDetailsFormSteps/PaymentDetailsMixin';

export default {
  name: 'PremiumImpactResult',

  components: {
    QuickLinks,
    PremiumAdjustmentTimeline,
    ApiLoading,
    PremiumImpactInformation,
    Alert,
    ProductSummaryCard,
  },

  mixins: [PaymentDetailsMixin],

  data() {
    return {
      apiStatus: {
        premiumAdjustmentTimeline: { loading: false, fail: false },
      },
      premiumTimelineData: [
        {
          paymentAmount: null,
          label: 'Difference to amount paid',
        },
        {
          paymentAmount: null,
          label: 'Next payment',
          date: null,
        },
        {
          paymentAmount: null,
          label: 'Payment onwards',
          date: null,
        },
      ],
      premiumAdjustmentPrices: null,
      productName: null,
      ErrorStrings,
    };
  },

  props: {
    premiumData: {
      type: Object,
      default() {
        return {
          // newPremiumAmount: 123.45, // mocks for testing
          isPremiumImpacted: false, // default false so that no impact by default (and no retrieve premium adjustments api call is made)
        };
      },
      oldPremiumAmount: {
        type: Number,
      },
      newPremiumAmount: {
        type: Number,
      },
      isPremiumImpacted: {
        type: Boolean,
      },
      // "Next payment date" entered in Step 1. Premium payment in format `yyyy-mm-dd`. Required for passing as `currentDate` to EAI - Retrieve Premium Adjustments API.
      paymentStartDate: {
        type: String,
      },
      oldFrequency: String,
      newFrequency: String,
    },
    /**
     * Flow name to be used in generating submit policy in process header.
     * This prop is only required if `submitPolicyInProcess` is true.
     */
    flowName: {
      type: String,
      required: false,
    },
  },

  beforeMount() {
    this.productName = this.membershipInfo?.productName;
  },

  mounted() {
    // only call fetch adjustment prices there is premium impact
    if (this.premiumData?.isPremiumImpacted) {
      this.getPremiumAdjustmentPrices();
    }
  },

  beforeUnmount() {
    // reset PaymentDetails vuex store as flow is complete
    this.resetPaymentDetails();
  },

  computed: {
    relatedArticles() {
      const data = this.refData;
      const relatedArticlesArray = [data?.relatedPage1, data?.relatedPage2, data?.relatedPage3];

      return relatedArticlesArray;
    },

    areThereArticlesToShow() {
      return Boolean(this.relatedArticles?.length);
    },

    // this is required to transfer refData api response to expected structure for PremiumAdjustmentTimeline.vue
    refDataInExpectedFormat() {
      let refData = cloneDeep(this.refData);
      refData = {
        ...refData,
        paymentAdjustmentInformationText: {
          informationText: refData?.premiumImpactPaymentAdjustedText?.informationText,
        },
      };
      return refData;
    },

    submitPolicyInProcessHeader() {
      return generateSubmitPolicyInProcessHeader(this.flowName, this.submitPolicyInProcess);
    },
  },

  methods: {
    formatToDisplayCurrencyPeriod,

    async getPremiumAdjustmentPrices() {
      this.apiStatus.premiumAdjustmentTimeline.loading = true;
      try {
        const currentMemberNumber = this.$route?.params?.policyNumber;
        this.premiumAdjustmentPrices = await PremiumImpactServices.getPremiumAdjustmentPrices(currentMemberNumber, true, this.premiumData?.paymentStartDate);

        this.premiumTimelineData[0].paymentAmount = this.premiumAdjustmentPrices.prorataPrice;

        this.premiumTimelineData[1].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
        this.premiumTimelineData[2].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;

        this.premiumTimelineData[1].date = this.premiumAdjustmentPrices.nextContributionDate;
        this.premiumTimelineData[2].date = this.premiumAdjustmentPrices.futureContributionDate;
      } catch (err) {
        logger.error(err);
        this.apiStatus.premiumAdjustmentTimeline.fail = true;
      } finally {
        this.apiStatus.premiumAdjustmentTimeline.loading = false;
      }
    },

    handlePayNowBtnClick(amt) {
      setNavigationState({ paymentAmount: amt });
      this.$router.push({ name: MY_COVER_MAKE_A_PAYMENT });
    },
  },
};
</script>

<style lang="scss" scoped>
.hidden {
  /** this class hides <h2> as per design but due to accessibility, <h2> is required, to pass accessibility, and screen reader, change opacity hence it is hidden and use as spacing between top content and quick links */
  opacity: 0.01; // use opacity instead of display none or visibility hidden to pass accessibility
  margin-bottom: 0;
  height: spacer(5); // for spacing between top content and quick links, which is 70px as per design suggested (20px is coming from <QuickLinks> style)
  overflow: hidden;
}
#member-portal {
  main {
    .feature-landing-page {
      .call-out {
        &.call-to-action {
          text-align: left;
        }
      }
    }
  }
}

table {
  width: 100%;

  tbody {
    width: inherit;
    display: inherit;

    tr {
      width: inherit;
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
